import {
    TCategoryData,
    TLocalizedMainCategory,
    TLocalizedNavCategory,
    TLanguage,
    TSivviNavCategory,
} from '@com/core/common/types/navigation/navigation';
import { TLocaleAPI } from '@com/core/common/types/locale';
import { MARKETPLACE } from '@com/core/common/baseConfig';
import {
    localizeMainCategory,
    localizeFooter,
    localizeNavCategory,
    localizeFashionMainCategory,
    localizeSivviNavCategory,
} from '@com/core/common/utils/localizeNav';
import { internalCatalogSVC as Product } from '@com/core/common/services/api';
import { reportEvent } from './sentry';

export default class NavConfig {
    static navConfig: Record<TLocaleAPI, TCategoryData> | unknown = {};

    public static async initialize(locale: TLocaleAPI): Promise<void> {
        // If we have it already, don't call again
        if (this.navConfig[locale]) {
            return;
        }

        // eslint-disable-next-line no-console
        console.log(`Initializing NavConfig for ${locale}`);

        const [lang, apiRegion] = locale.split('-');

        try {
            const nonLocalizedNavConfig = (await import(`@com/core/${MARKETPLACE}/data`)).default;
            const categoriesData = nonLocalizedNavConfig[apiRegion];

            switch (MARKETPLACE) {
                case 'noon':
                case 'kul': {
                    this.navConfig[locale] = {
                        footerCategories: localizeFooter(lang as TLanguage, categoriesData.footerCategories),
                        mainCategories: localizeMainCategory(
                            lang as TLanguage,
                            categoriesData.mainCategories as TLocalizedMainCategory[],
                        ),
                        navCategories: localizeNavCategory(
                            lang as TLanguage,
                            categoriesData.navCategories as TLocalizedNavCategory[],
                        ),
                    };
                    break;
                }
                case 'sivvi': {
                    const fashionCategories = (await Product(locale).getCategories())?.data?.root || [];

                    this.navConfig[locale] = {
                        footerCategories: localizeFooter(lang as TLanguage, categoriesData.footerCategories),
                        mainCategories: localizeFashionMainCategory(
                            lang as TLanguage,
                            categoriesData.mainCategories as TLocalizedMainCategory[],
                        ),
                        navCategories: localizeSivviNavCategory(
                            lang as TLanguage,
                            categoriesData.navCategories as TSivviNavCategory,
                        ),
                        fashionCategories,
                    };

                    break;
                }
                default:
                    reportEvent(
                        '☢️ Nav Config fetch failed: Marketplace did not match',
                        'navConfig.initialize',
                        'critical',
                        {
                            locale,
                        },
                    );
                    break;
            }
        } catch (error) {
            reportEvent('☢️ Nav Config fetch failed: Network issue', 'navConfig.initialize', 'critical', error);
        }
    }

    public static async getNavConfig(locale: TLocaleAPI): Promise<TCategoryData> {
        if (!this.navConfig[locale]) {
            await this.initialize(locale);
            reportEvent('Fetching navConfig', 'getNavConfig()', 'info', { locale });
        }

        return this.navConfig[locale];
    }
}
